import { Dayjs } from 'dayjs';
import { Program } from '../../../../pages/eligibility/types';
import { DateString, OptionType } from '../../../../utils/type';
import { ApiMessage, ApiResponseHandler } from '../../../ApiResponseHandler';
import { REFERRAL_PROGRAM_KEY, THOM_BASE_URL } from '../../../Constant';
import ThomApiClient from '../../../ThomApiClient';

export type ConsentForChangeType = {
  isEligibleUntilThirdBirthday: string;
  eligibleTool: OptionType;
  assessmentCompletionDate: string | null;
  reEstablishedDate: string | null;
  parentInitial: OptionType;
  isChecked: boolean;
  participated: boolean;
  agreeDisagree: string;
  exceptionList: string;
  consent: boolean;
};

export interface ConsentInsurance {
  companyId: string | null;
  companyName: string | null;
  otherCompanyName: string | null;
  policyNumber: string | null;
  subscriberName: string | null;
  consentToInsurance: string;
  insuranceNotes: string;
  isInsuranceMigrated: boolean;
}

export interface ConsentDetailsType {
  schoolNameMandatory?: boolean;
  source?: string;
  type: string;
  name: string;
  consentId: string;
  version: number;
  signedDate: string | Date | null;
  revokedDate: DateString;
  status: string;
  requestedDate: string;
  requester: ConsentRequester;
  client: ConsentClient;
  contact: ConsentClientContact[];
  refusal: boolean;
  signatureType?: string;
  signature: ConsentClientSignature[];
  selectedContact: string[];
  programProposes: number[] | null;
  communicationMode: number[] | null;
  purpose: number[] | null;
  serviceDeliveryPlan: ConsentForChangeType;
  programDetails: Program;
  ifsp: number[] | null;
  eligibility: number[] | null;
  other: boolean;
  otherContactName: string;
  othersComment: string;
  summary: string;
  description: string;
  selectedLanguage: string;
  initials: string | null;
  dateMailed: string | null | Dayjs;
  consentPwnType: {
    id: string | null;
    pwnType: string | null;
    pwnDescription: string | null;
  };
  uiVersion: number;
  hsaConsentVersion?: number | null;
  consentInsurance: ConsentInsurance | null;
  revokeSignature: ConsentClientSignature | null;
}

export interface ConsentRequester {
  name: string;
  email: string;
  phone: string;
}

export interface ConsentClient {
  id: string;
  name: string;
  dphId: string | null;
  gender: string;
  dob: string;
  address: string | null;
}

export interface ConsentClientContact {
  id: string;
  name: string;
  address: string | null;
  email: string;
  phone: string;
  type?: string;
  isGuardian: boolean;
  isPrimary: boolean;
  isLegalGuardian: boolean;
}

export interface ConsentClientSignature {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  relationship: string;
  email: string;
  signature: string;
  verbal: boolean;
  paperConsent?: boolean;
  date?: string | null;
  consentSignedDate?: string | null;
  license?: string;
  rowKey?: string;
  partitionKey?: string;
  isPrimary: boolean;
  isLegal: boolean;
  isLatest?: boolean;
  isGuardian: boolean;
}
export interface ConsentLeaSignature {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  relationship: string;
  email: string;
  signature: string;
  verbal: boolean;
  date?: string;
  consentSignedDate?: string | null;
  license?: string;
  rowKey?: string;
  partitionKey?: string;
  isPrimary?: boolean;
  isLegal?: boolean;
  isLatest?: boolean;
}
export interface ConsentProgramDetails {
  name: string;
  phoneNumber: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  faxNumber: string;
  website: string | null;
  imageUrl: string | null;
}

export type ConsentListData = {
  consentId: string;
  consentType: string;
  consentName: string;
  consentDate: string;
  expirationDate: string;
  status: string;
  isSigned: boolean;
  permissionGrantedBy: string;
  permissionGrantedTo: string;
  version: number;
  uiVersion: number;
  isSchoolNameMandatory?: boolean;
  consentInsurance?: ConsentInsurance | null;
};

export type ConsentList = {
  consents: ConsentListData[];
  totalConsentsCount: number;
};

export interface ConsentType {
  id: string;
  label: string;
  isSelected: boolean;
  uiVersion: number;
}

export const updateConsent = async (
  type: string,
  consentId: string,
  params: ConsentDetailsType,
  version?: number | null,
): Promise<ApiMessage> => {
  const referralProgramId = localStorage.getItem(REFERRAL_PROGRAM_KEY);
  return ApiResponseHandler(
    await ThomApiClient.getInstance().put<ConsentDetailsType, ApiMessage>(
      `consent?type=${type}&consentId=${consentId}&version=${version}&referralProgramId=${
        referralProgramId ?? ''
      }`,
      params,
    ),
  );
};

export const getConsentList = async (
  sort: string,
  sortType: string,
  type: string,
): Promise<ConsentList> => {
  const referralProgramId = localStorage.getItem(REFERRAL_PROGRAM_KEY);
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<ConsentList>(
      `consent/list?sort=${sort}&source=${type}&sortType=${sortType}&referralProgramId=${
        referralProgramId ?? ''
      }`,
    ),
  );
};

export const createConsents = async (
  ids: ConsentType[],
): Promise<ApiMessage> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().post<ConsentType[], ApiMessage>(
      `consents`,
      ids,
    ),
  );
};

export const deleteConsent = async (consentId: string): Promise<ApiMessage> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().delete<ApiMessage>(
      `deleteConsent?consentId=${consentId}`,
    ),
  );
};

export const getConsentDetails = async (
  type: string,
  version: number,
  consentId?: string,
): Promise<ConsentDetailsType> => {
  const referralProgramId = localStorage.getItem(REFERRAL_PROGRAM_KEY);
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<ConsentDetailsType>(
      `consent?type=${type}&consentId=${consentId}&version=${version}&referralProgramId=${
        referralProgramId ?? ''
      }`,
    ),
  );
};

export const downloadConsent = async (
  type: string,
  consentId: string,
  localTimeZone: string,
  language = 'en',
): Promise<Blob> => {
  const referralProgramId = localStorage.getItem(REFERRAL_PROGRAM_KEY);
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<Blob>(
      `consent/download?type=${type}&consentId=${consentId}&timezone=${localTimeZone}&referralProgramId=${
        referralProgramId ?? ''
      }&language=${language}`,
      {},
      {
        baseURL: THOM_BASE_URL,
        responseType: 'blob',
      },
    ),
  );
};

export const getConsentDetailsLEA = async (
  type: string,
  consentId?: string,
): Promise<ConsentDetailsType> => {
  const referralProgramId = localStorage.getItem(REFERRAL_PROGRAM_KEY);
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<ConsentDetailsType>(
      `consent?type=${type}&consentId=${consentId}&referralProgramId=${
        referralProgramId ?? ''
      }`,
    ),
  );
};

export const downloadLeaReferral = async (
  type: string,
  consentId: string,
  localTimeZone: string,
  language = 'en',
): Promise<Blob> => {
  const referralProgramId = localStorage.getItem(REFERRAL_PROGRAM_KEY);
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<Blob>(
      `lea-consent/download?type=${type}&consentId=${consentId}&timezone=${localTimeZone}&referralProgramId=${
        referralProgramId ?? ''
      }&language=${language}`,
      {},
      {
        baseURL: THOM_BASE_URL,
        responseType: 'blob',
      },
    ),
  );
};
